import { ZpaAccesses } from "@p0security/integrations-zpa-shared/accesses";
import type { ArgsOf, RequestOf } from "@p0security/shared/types/access/index";
import { Access, Delegate } from "@p0security/shared/types/access/index";
import type { EmptyAccesses } from "@p0security/shared/types/lifecycle";

import { commonAllAccess } from "./all.ts";
import { commonGroupAccess, commonSshAccess } from "./group.ts";
import {
  groupNameParameter,
  networkAccessParameter,
  parentParameter,
  publicKeyParameter,
  regionParameter,
  requiredProviderParameter,
  sessionDestinationParameter,
  sudoParameter,
  zoneParameter,
} from "./parameters.ts";
import { commonParentAccess } from "./parent.ts";

const parentLabel = "Environment";

export const SelfHostedSshAccesses = {
  session: Access({
    ...commonSshAccess,
    maturity: "ga",
    delegation: {
      "zscaler-application-segment": Delegate({
        resource: "zscaler-private-access",
        key: "application-segment",
        accesses: ZpaAccesses,
      }),
    },
    generated: {
      linuxUserName: {
        type: "string",
        label: "Linux user name",
        description:
          "Linux user name that should be used to connect to the instance",
      },
      publicKey: {
        type: "string",
        optional: true,
      },
    },
    parameters: {
      provider: {
        ...requiredProviderParameter,
        choices: ["self-hosted"] as const,
      },
      destination: sessionDestinationParameter,
      parent: parentParameter(parentLabel),
      group: groupNameParameter,
      sudo: sudoParameter,
      region: regionParameter,
      publicKey: publicKeyParameter,
      zone: zoneParameter,
      resource: {
        type: "object",
        label: "Data",
        properties: {
          clientId: {
            type: "generated",
            label: "Client ID",
          },
          environmentId: {
            type: "generated",
            label: "Environment ID",
          },
          hostId: {
            type: "generated",
            label: "Host ID",
          },
          hostname: {
            type: "generated",
            label: "Hostname",
          },
          publicIp: {
            type: "generated",
            label: "Public IP",
          },
          fingerprint: {
            type: "generated",
            label: "Fingerprint",
          },
          groupTag: {
            type: "object",
            label: "Group tag",
            optional: true,
            properties: {
              key: {
                type: "generated",
                label: "Group tag key",
              },
              value: {
                type: "generated",
                label: "Group tag value",
              },
            },
          },
        },
      },
      // Shared with unknown's Access definition — see networkAccessParameter in parameters.ts for context on why
      networkAccess: networkAccessParameter,
    },
    render: {
      locator: "parent",
      policy: "sudo",
      resource: "destination",
    } as const,
  }),
  group: commonGroupAccess(parentLabel),
  all: commonAllAccess,
  parent: commonParentAccess(parentLabel),
} satisfies EmptyAccesses;

export type SelfHostedSshAccesses = typeof SelfHostedSshAccesses;
export type SelfHostedSshAccess =
  SelfHostedSshAccesses[keyof SelfHostedSshAccesses];
export type SelfHostedSshAccessArgs = ArgsOf<SelfHostedSshAccesses>;

export type SelfHostedSshSessionAccess = SelfHostedSshAccesses["session"];
export type SelfHostedSshSessionAccessArgs = ArgsOf<SelfHostedSshSessionAccess>;
export type SelfHostedSshSessionRequest = RequestOf<SelfHostedSshSessionAccess>;

export type SelfHostedSshGroupAccess = SelfHostedSshAccesses["group"];
export type SelfHostedSshGroupAccessArgs = ArgsOf<SelfHostedSshGroupAccess>;

export type SelfHostedSshParentAccess = SelfHostedSshAccesses["parent"];
export type SelfHostedSshParentAccessArgs = ArgsOf<SelfHostedSshParentAccess>;

export type SelfHostedSshAllAccess = SelfHostedSshAccesses["all"];
export type SelfHostedSshAllAccessArgs = ArgsOf<SelfHostedSshAllAccess>;
